import { useMemo, useRef } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';

import { useContainerWidth } from '../hooks/use-container-width';
import { buildXAxisConfig } from '../lib/charts/axis';
import { buildLegendItems } from '../lib/charts/legend';
import { getPalette } from '../lib/charts/palettes';
import { cn } from '../lib/utils';
import { CodeBlock } from './CodeBlock';
import {
  CHART_PLOT_HEIGHT,
  CHART_THEME_SELECTORS,
  type ChartConfig,
  ChartContext,
  ChartLegendList,
  ChartTooltip,
  ChartTooltipContent,
} from './ui/chart';

interface ChartBlockProps {
  content: string;
  metadata?: unknown;
}

interface ChartDataset {
  label?: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string | string[];
}

interface ChartSpec {
  type: string;
  data: {
    labels: string[];
    datasets: ChartDataset[];
  };
}

function parseChartSpec(content: string): ChartSpec | null {
  try {
    const parsed: unknown = JSON.parse(content);
    if (!parsed || typeof parsed !== 'object') return null;
    const spec = parsed as Record<string, unknown>;
    if (typeof spec['type'] !== 'string') return null;
    if (!spec['data'] || typeof spec['data'] !== 'object') return null;
    const data = spec['data'] as Record<string, unknown>;
    if (!Array.isArray(data['labels']) || !Array.isArray(data['datasets'])) return null;
    return parsed as ChartSpec;
  } catch {
    return null;
  }
}

function toRechartsData(spec: ChartSpec): Record<string, unknown>[] {
  return spec.data.labels.map((label, i) => {
    const point: Record<string, unknown> = { name: label };
    for (const [dsIndex, ds] of spec.data.datasets.entries()) {
      const key = ds.label ?? `series${dsIndex}`;
      point[key] = ds.data[i] ?? 0;
    }
    return point;
  });
}

/**
 * Resolve colors for a dataset. With the new `send_chart` tool, colors come
 * from semantic palettes (not the agent). Legacy specs that still include
 * backgroundColor/borderColor are handled for backwards compatibility.
 */
function getColors(ds: ChartDataset, count: number, palette: string[]): string[] {
  const source = ds.borderColor ?? ds.backgroundColor;
  if (Array.isArray(source)) return source;
  if (typeof source === 'string') return Array(count).fill(source) as string[];
  return palette;
}

/** Resolve the stroke/legend color for one dataset at its position in a multi-series chart. */
function colorForDataset(ds: ChartDataset, index: number, palette: string[]): string {
  const perSeriesFallback = palette[index % palette.length] ?? '#8b5cf6';
  const source = ds.borderColor ?? ds.backgroundColor;
  if (typeof source === 'string') return source;
  if (Array.isArray(source)) return source[0] ?? perSeriesFallback;
  return perSeriesFallback;
}

function buildChartConfig(spec: ChartSpec, palette: string[]): ChartConfig {
  const config: ChartConfig = {};
  for (const [index, ds] of spec.data.datasets.entries()) {
    const key = ds.label ?? `series${index}`;
    config[key] = {
      label: ds.label ?? `Series ${index + 1}`,
      color: colorForDataset(ds, index, palette),
    };
  }
  return config;
}

/** Build CSS custom properties from a ChartConfig so var(--color-{key}) works. */
function buildChartCssVars(config: ChartConfig): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(config)) {
    vars[`--color-${key}`] = value.color;
  }
  return vars;
}

export function ChartBlock({ content, metadata }: ChartBlockProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartWidth = useContainerWidth(containerRef);

  const spec = useMemo(() => parseChartSpec(content), [content]);
  if (!spec) return <CodeBlock content={content} metadata={{ language: 'json' }} />;

  const meta = metadata as Record<string, unknown> | undefined;
  const paletteName = meta?.['palette'] as string | undefined;
  const rawTitle = meta?.['title'];
  const title = typeof rawTitle === 'string' && rawTitle.length > 0 ? rawTitle : null;
  const palette = getPalette(paletteName);
  const chartConfig = buildChartConfig(spec, palette);

  return (
    <ChartBlockInner
      spec={spec}
      chartConfig={chartConfig}
      palette={palette}
      containerRef={containerRef}
      chartWidth={chartWidth}
      content={content}
      title={title}
    />
  );
}

interface ChartBlockInnerProps {
  spec: ChartSpec;
  chartConfig: ChartConfig;
  palette: string[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  chartWidth: number;
  content: string;
  title: string | null;
}

function ChartBlockInner({
  spec,
  chartConfig,
  palette,
  containerRef,
  chartWidth,
  content,
  title,
}: ChartBlockInnerProps): React.ReactElement {
  const cssVars = useMemo(() => buildChartCssVars(chartConfig), [chartConfig]);
  const data = useMemo(() => toRechartsData(spec), [spec]);
  const axis = useMemo(() => buildXAxisConfig(spec.data.labels, chartWidth), [spec, chartWidth]);
  const chartType = spec.type.toLowerCase();

  if (chartType === 'pie' || chartType === 'doughnut') {
    const ds = spec.data.datasets[0];
    if (!ds) return <CodeBlock content={content} metadata={{ language: 'json' }} />;
    const key = ds.label ?? 'value';
    const pieData = data.map((d) => ({ name: d['name'] as string, value: d[key] as number }));
    const colors = getColors(ds, pieData.length, palette);
    const legendItems = buildLegendItems(
      pieData.map((d) => d.name),
      colors,
    );

    return (
      <ChartContext.Provider value={{ config: chartConfig }}>
        <div
          ref={containerRef}
          className={cn(
            'flex w-full flex-col items-center overflow-hidden rounded-lg border border-border bg-muted p-4 text-xs',
            CHART_THEME_SELECTORS,
          )}
          style={cssVars}
        >
          {title && <div className="mb-2 text-sm font-medium text-foreground">{title}</div>}
          {chartWidth > 0 && (
            <PieChart width={chartWidth} height={CHART_PLOT_HEIGHT}>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={chartType === 'doughnut' ? '40%' : 0}
                outerRadius="80%"
                strokeWidth={0}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={colors[i % colors.length]} />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent nameKey="name" hideLabel />} />
            </PieChart>
          )}
          {chartWidth > 0 && <ChartLegendList items={legendItems} />}
        </div>
      </ChartContext.Provider>
    );
  }

  const ChartComponent = chartType === 'area' ? AreaChart : chartType === 'line' ? LineChart : BarChart;
  const isMultiSeriesBar = chartType === 'bar' && spec.data.datasets.length > 1;
  const legendItems = buildLegendItems(
    spec.data.datasets.map((ds, i) => ds.label ?? `Series ${i + 1}`),
    spec.data.datasets.map((ds, i) => colorForDataset(ds, i, palette)),
  );

  return (
    <ChartContext.Provider value={{ config: chartConfig }}>
      <div
        ref={containerRef}
        className={cn(
          'flex w-full flex-col items-center overflow-hidden rounded-lg border border-border bg-muted p-4 text-xs',
          CHART_THEME_SELECTORS,
        )}
        style={cssVars}
      >
        {title && <div className="mb-2 text-sm font-medium text-foreground">{title}</div>}
        {chartWidth > 0 && (
          <ChartComponent data={data} width={chartWidth} height={CHART_PLOT_HEIGHT + axis.axisHeight}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} tickLine={false} {...axis.props} />
            <YAxis tick={{ fontSize: 12 }} tickLine={false} />
            <ChartTooltip content={<ChartTooltipContent labelFormatter={axis.tooltipLabelFormatter} />} />
            {spec.data.datasets.map((ds, dsIndex) => {
              const dataKey = ds.label ?? `series${dsIndex}`;
              const color = colorForDataset(ds, dsIndex, palette);
              if (chartType === 'area') {
                return (
                  <Area
                    key={dataKey}
                    type="monotone"
                    dataKey={dataKey}
                    stroke={color}
                    fill={color}
                    fillOpacity={0.3}
                    strokeWidth={2}
                    dot={false}
                  />
                );
              }
              if (chartType === 'line') {
                return (
                  <Line key={dataKey} type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
                );
              }
              // Bar branch: multi-series uses one color per series (matches legend).
              // Single-series preserves per-category cycling so bars aren't all one color.
              if (isMultiSeriesBar) {
                return <Bar key={dataKey} dataKey={dataKey} radius={[4, 4, 0, 0]} fill={color} />;
              }
              const barColors = getColors(ds, data.length, palette);
              return (
                <Bar key={dataKey} dataKey={dataKey} radius={[4, 4, 0, 0]}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={barColors[i % barColors.length]} />
                  ))}
                </Bar>
              );
            })}
          </ChartComponent>
        )}
        {chartWidth > 0 && <ChartLegendList items={legendItems} />}
      </div>
    </ChartContext.Provider>
  );
}
